/**
 * A segmented control: one of a few, side by side — Ours · All, Auto ·
 * Manual, Value · Balanced · Premium.
 *
 * A radio group with one tab stop: the arrow keys move the choice, as radios
 * do. The chosen segment is a raised plate in the strong ink; the others sit
 * quiet in the caption ink. `block` stretches it across its container, the
 * segments sharing the width. `showLabel` sets its label above it, as a
 * field's — the globe's airport levels — the group named by it. A segment
 * given a `hint` explains itself in a hover card, on hover and on focus — a
 * route's price strategies — and the hint describes it to assistive
 * technology. `resting` rests every segment while a change is on its way; an
 * option's own `resting` rests it alone, its hint saying why — Manual, on a
 * flight priced on Auto, which a moved fare makes Manual. The arrows pass over
 * what rests.
 *
 *   <SegmentedControl label="Price mode" value={mode} onChange={setMode}
 *                     options={[{ value: 'auto', label: 'Auto' }, { value: 'manual', label: 'Manual' }]} />
 */

import { useId, useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

import utilities from '../../styles/utilities.module.css';
import { HoverCard, HoverCardNote, HoverCardTitle } from './hover-card';
import styles from './segmented-control.module.css';

interface SegmentOption<V extends string> {
  value: V;
  label: ReactNode;
  /** What choosing it does, told in a card on hover and focus: its name, and a sentence. */
  hint?: { title: string; note: string };
  /** It cannot be chosen now: it rests, and its hint says why. */
  resting?: boolean;
}

interface SegmentedControlProps<V extends string> {
  /** Names the group. */
  label: string;
  value: V;
  options: readonly SegmentOption<V>[];
  onChange: (value: V) => void;
  block?: boolean;
  /** Show the label above it, as a field's. */
  showLabel?: boolean;
  /** Every segment rests: a change is on its way. */
  resting?: boolean;
  className?: string;
}

export function SegmentedControl<V extends string>({
  label,
  value,
  options,
  onChange,
  block = false,
  showLabel = false,
  resting = false,
  className = '',
}: SegmentedControlProps<V>) {
  const group = useRef<HTMLDivElement>(null);
  const labelId = useId();
  const choosable = (option: SegmentOption<V>) => !resting && !option.resting;
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    // The arrows pass over what cannot be chosen now, and choose nothing while it all rests.
    const index = options.findIndex((option) => option.value === value);
    for (let i = 1; i < options.length; i++) {
      const next = (index + step * i + options.length * i) % options.length;
      if (!choosable(options[next])) continue;
      onChange(options[next].value);
      group.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[next]?.focus();
      return;
    }
  };
  const control = (
    <div
      ref={group}
      role="radiogroup"
      aria-label={showLabel ? undefined : label}
      aria-labelledby={showLabel ? labelId : undefined}
      className={[styles.group, block ? styles.block : '', showLabel ? '' : className].filter(Boolean).join(' ')}
      onKeyDown={onKeyDown}
    >
      {options.map((option) => {
        const chosen = option.value === value;
        const segment = (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={chosen}
            aria-disabled={choosable(option) ? undefined : true}
            tabIndex={chosen ? 0 : -1}
            className={[styles.segment, chosen ? styles.chosen : ''].filter(Boolean).join(' ')}
            onClick={() => {
              if (choosable(option)) onChange(option.value);
            }}
          >
            {option.label}
          </button>
        );
        if (!option.hint) return segment;
        return (
          <HoverCard
            key={option.value}
            label={option.hint.title}
            description={option.hint.note}
            placement="below"
            stretch={block}
            className={styles.hinted}
            card={
              <>
                <HoverCardTitle>{option.hint.title}</HoverCardTitle>
                <HoverCardNote>{option.hint.note}</HoverCardNote>
              </>
            }
          >
            {segment}
          </HoverCard>
        );
      })}
    </div>
  );
  if (!showLabel) return control;
  return (
    <div className={[utilities.fieldStack, className].filter(Boolean).join(' ')}>
      <span id={labelId} className={utilities.fieldLabel}>
        {label}
      </span>
      {control}
    </div>
  );
}
